import { Events } from '@Context/common/general/context';
import { useApi } from '@hooks';
import useGlobalContext from '@hooks/common/general/use-global-context';
import AccountRoles from '@Shared/enums/AccountRoles';
import { Api } from '@Shared/schemas';
import { RoleRecord } from '@Shared/schemas/accounts/accountSchemas';
import { getRoleLevel } from '@Shared/utils/rolePermissions';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AddProfileContextType {
  roles: RoleRecord[];
  dob: Date | null;
  handleDobChange: (date: Date | null) => void;
  handleCancel: () => void;
  MIN_AGE: number;
  latestDob: Date;
}

const AddProfileContext = createContext<AddProfileContextType | undefined>(undefined);

const MIN_AGE = 13;

export const AddProfileProvider = ({ children }: { children: ReactNode }) => {
  const { globalState } = useGlobalContext();
  const navigate = useNavigate();
  const isWeb = !globalState.device.isUnityEmbedded;
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const latestDob = new Date(Date.now() - MIN_AGE * 365.25 * 24 * 60 * 60 * 1000);
  const [dob, setDob] = useState<Date | null>(latestDob);

  const { call: getRoles } = useApi({
    api: Api.Account.GetRoles,
    onSuccess: ({ data }) => {
      if (data && globalState.account?.user_role) {
        const currentUserRoleLevel = getRoleLevel(globalState.account.user_role);
        // Filter roles to only include those below the current user's role level
        const availableRoles = data.filter((role) => {
          const roleEnum = role.name as AccountRoles;
          return getRoleLevel(roleEnum) < currentUserRoleLevel;
        });
        // Sort roles by hierarchy level (highest first)
        const sortedRoles = availableRoles.sort((a, b) => {
          const aLevel = getRoleLevel(a.name as AccountRoles);
          const bLevel = getRoleLevel(b.name as AccountRoles);
          return bLevel - aLevel;
        });

        setRoles(sortedRoles);
      }
    },
  });

  const handleNavigate = (path: string) => {
    if (isWeb) {
      navigate(path);
    } else {
      Events.app.navigate.emit({ path });
    }
  };

  const handleDobChange = (date: Date | null) => {
    setDob(date);
  };

  const handleCancel = () => {
    handleNavigate('/select-profile');
  };

  useEffect(() => {
    getRoles();
  }, [globalState.account?.user_role]);

  const value = {
    roles,
    dob,
    handleDobChange,
    handleCancel,
    MIN_AGE,
    latestDob,
  };

  return <AddProfileContext.Provider value={value}>{children}</AddProfileContext.Provider>;
};

export const useAddProfile = () => {
  const context = useContext(AddProfileContext);
  if (context === undefined) {
    throw new Error('useAddProfile must be used within an AddProfileProvider');
  }
  return context;
};
